import { createRef, Component } from 'react';
import type {
  ReactNode,
} from 'react';
import styled from 'styled-components';

import isEqual from 'lodash.isequal';

import { createDropdownMenu } from '../createDropdownMenu';
import type {
  DropdownMenuItemType,
} from '../createDropdownMenu';
import { EllipsisDropdown } from '../EllipsisDropdown/EllipsisDropdown';

import { Tab } from './Tab';

import type {
  RenderTab,
  TabId,
  TabType,
} from './types';

export const showMoreLength = 100;

const StyledTabs = styled.div({
  display: 'flex',
  position: 'relative',
});

const StyledRendered = styled.div(({
  theme: {
    tabs,
  },
}) => ({
  overflow: 'hidden',
  height: tabs.FULL_HEIGHT,
}));

export const StyledUl = styled.ul({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  whiteSpace: 'nowrap',
  height: '100%',
});

export type StyledDropdownProps = {
  $hasCurrent: boolean;
};

export const StyledDropdown = styled.div<StyledDropdownProps>(({
  $hasCurrent,

  theme: {
    colors,
    tabs,
  },
}) => ({
  flex: 1,
  display: 'flex',
  position: 'relative',
  alignItems: 'center',
  justifyContent: 'flex-end',
  borderBottomWidth: tabs.BORDER_BOTTOM_WIDTH,
  borderBottomStyle: 'solid',
  borderBottomColor: colors.NEUTRAL_INACTIVE,

  '::before': {
    content: $hasCurrent ? '""' : undefined,
    position: 'absolute',
    right: 0,
    bottom: 0,
    width: 24,
    height: tabs.ACTIVE_BORDER_WIDTH,
    backgroundColor: colors[tabs.ACTIVE_BORDER_COLOR],
  },
}));

const StyledTabWrapper = styled.li(({
  theme: {
    colors,
    tabs,
  },
}) => ({
  boxSizing: 'border-box',
  display: 'inline-block',
  verticalAlign: 'bottom',
  borderBottomWidth: tabs.BORDER_BOTTOM_WIDTH,
  borderBottomStyle: 'solid',
  borderBottomColor: colors.NEUTRAL_INACTIVE,
  height: '100%',

  '& + &': {
    paddingLeft: tabs.GAP,
  },
}));

const defaultRenderContent = (content: ReactNode) => content;

export type TabsProps = {
  /**
   * Массив табов для рендера
   */
  tabs: TabType[];
  /**
   * id выбранного таба
   */
  current?: TabId;

  /**
   * Пересчитывать ли состояние скрытий/показа табов при изменении размеров окна
   */
  recountOnResize?: boolean;

  /**
   * Функция рендера таба
   * @param {Object} renderProps
   * @param {string} renderProps.className
   * @param {Function} renderProps.onClick
   * @param {ReactNode} renderProps.label - заголовок таба
   * @param {Object} renderProps.tab - объект таба
   */
  renderTab?: RenderTab;

  /**
   * Обработчик изменения текущего таба
   * @param {string|number} tabId - id выбранного таба
   */
  setCurrentTab?: (tabId: TabId) => void;
  /**
   * Функция рендера табов для возможности создания обёртки
   */
  renderContent?: (content: ReactNode) => ReactNode;
};

export type State = {
  hasOverflow: boolean;
  numberOfRendered: number | null;
};

export class Tabs extends Component<TabsProps, State> {
  // eslint-disable-next-line react/static-property-placement
  static defaultProps = {
    current: undefined,
    recountOnResize: false,
    renderContent: defaultRenderContent,
    renderTab: undefined,
    setCurrentTab: undefined,
  };

  constructor(props: TabsProps) {
    super(props);

    this.state = {
      hasOverflow: false,
      numberOfRendered: null,
    };
  }

  async componentDidMount(): Promise<void> {
    await this.checkOverflow();

    const {
      recountOnResize,
    } = this.props;

    if (recountOnResize) {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      window.addEventListener('resize', this.reloadOverflow);
    }
  }

  async componentDidUpdate(oldProps: TabsProps): Promise<void> {
    const {
      tabs,
    } = this.props;

    if (!isEqual(oldProps.tabs, tabs)) {
      await this.reloadOverflow();
    }
  }

  componentWillUnmount(): void {
    const {
      recountOnResize,
    } = this.props;

    if (recountOnResize) {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      window.removeEventListener('resize', this.reloadOverflow);
    }
  }

  async checkOverflow(): Promise<void> {
    const renderedTabsNode = this.renderedRef.current;

    if (!renderedTabsNode) {
      return;
    }

    const hasOverflow = renderedTabsNode.scrollWidth > renderedTabsNode.offsetWidth;

    if (hasOverflow) {
      await this.countVisibleTabs();
    }
  }

  async countVisibleTabs(): Promise<void> {
    const renderedTabsNode = this.renderedRef.current;
    const liNodes = this.renderedUl.current
      ? this.renderedUl.current.children
      : [];

    const widthLimit = (renderedTabsNode?.offsetWidth || 0) - showMoreLength;

    let liWidthAcc = 0;
    let hasNotResult = true;
    let numberOfRendered = 0;
    for (
      let i = 0, l = liNodes.length;
      i < l && hasNotResult;
      ++i
    ) {
      liWidthAcc += (liNodes[i] as HTMLLIElement).offsetWidth;

      if (liWidthAcc > widthLimit) {
        hasNotResult = false;
      } else {
        ++numberOfRendered;
      }
    }

    // eslint-disable-next-line @typescript-eslint/await-thenable
    await this.setState({
      hasOverflow: true,
      numberOfRendered,
    });
  }

  reloadOverflow = async (): Promise<void> => {
    // eslint-disable-next-line @typescript-eslint/await-thenable
    await this.setState({
      hasOverflow: false,
      numberOfRendered: null,
    });

    await this.checkOverflow();
  };

  renderedRef = createRef<HTMLDivElement>();

  renderedUl = createRef<HTMLUListElement>();

  renderTab(tab: TabType): ReactNode {
    const {
      current,
      setCurrentTab,
      renderTab,
    } = this.props;

    const isCurrent = current === tab.id;

    return (
      <StyledTabWrapper
        key={tab.id}
      >
        <Tab
          tab={tab}
          isDropdownItem={false}
          isCurrent={isCurrent}
          setCurrentTab={setCurrentTab}
          renderTab={renderTab}
        />
      </StyledTabWrapper>
    );
  }

  getDropdownTabs(): {
    tabs: DropdownMenuItemType[];
    hasCurrent: boolean;
  } {
    const {
      tabs,
      current,

      setCurrentTab,
      renderTab,
    } = this.props;

    const {
      numberOfRendered,
    } = this.state;

    let hasCurrent = false;
    const resTabs: DropdownMenuItemType[] = [];

    for (let i = (numberOfRendered || 0), l = tabs.length; i < l; ++i) {
      const tab = tabs[i];

      const isCurrent = current === tab.id;

      if (isCurrent) {
        hasCurrent = true;
      }

      resTabs.push({
        render: () => (
          <Tab
            isCurrent={isCurrent}
            isDropdownItem
            renderTab={renderTab}
            setCurrentTab={setCurrentTab}
            tab={tab}
          />
        ),
        label: tab.title,
      });
    }

    return {
      tabs: resTabs,
      hasCurrent,
    };
  }

  renderTabs(): ReactNode[] {
    const {
      tabs,
    } = this.props;

    const {
      hasOverflow,
      numberOfRendered,
    } = this.state;

    const renderedLength = hasOverflow
      ? numberOfRendered
      : tabs.length;

    if (typeof renderedLength !== 'number') {
      return [];
    }

    const res: ReactNode[] = [];

    for (let i = 0; i < renderedLength; ++i) {
      res.push(this.renderTab(tabs[i]));
    }

    return res;
  }

  renderDropdown(): ReactNode {
    const {
      tabs,
      hasCurrent,
    } = this.getDropdownTabs();

    return (
      <StyledDropdown
        $hasCurrent={hasCurrent}
      >
        <EllipsisDropdown
          dropdown={{
            ...createDropdownMenu(tabs),
            placement: 'bottom-end',
          }}
        />
      </StyledDropdown>
    );
  }

  render(): ReactNode {
    const {
      renderContent = defaultRenderContent,
    } = this.props;

    const {
      hasOverflow,
    } = this.state;

    return (
      <StyledTabs>
        <StyledRendered
          ref={this.renderedRef}
        >
          {
            renderContent(
              <StyledUl
                ref={this.renderedUl}
              >
                {this.renderTabs()}
              </StyledUl>,
            )
          }
        </StyledRendered>

        {hasOverflow && this.renderDropdown()}
      </StyledTabs>
    );
  }
}
